import { DataTableSurface, Skeleton } from 'design-system';

import generateSkeletonKeys from '@/shared/lib/format/generateSkeletonKeys';

type SkeletonColumnId = 'selection' | 'title' | 'code' | 'period' | 'metadata';

const skeletonRowKeys = generateSkeletonKeys(16);
const tableGridClassName =
  'grid grid-cols-[2rem_minmax(12rem,3fr)_minmax(5rem,1fr)_minmax(6rem,1fr)_minmax(7rem,1fr)]';

const skeletonColumns: ReadonlyArray<{
  id: SkeletonColumnId;
  headerWidth: string;
}> = [
  { id: 'selection', headerWidth: 'w-3' },
  { id: 'title', headerWidth: 'w-20' },
  { id: 'code', headerWidth: 'w-10' },
  { id: 'period', headerWidth: 'w-12' },
  { id: 'metadata', headerWidth: 'w-16' }
];

const rowWidthPatterns = [
  {
    selection: 'w-3',
    title: 'w-4/5',
    code: 'w-1/2',
    period: 'w-2/3',
    metadata: 'w-3/5'
  },
  {
    selection: 'w-3',
    title: 'w-2/3',
    code: 'w-3/5',
    period: 'w-1/2',
    metadata: 'w-2/3'
  },
  {
    selection: 'w-3',
    title: 'w-3/4',
    code: 'w-2/5',
    period: 'w-3/4',
    metadata: 'w-1/2'
  }
] as const satisfies ReadonlyArray<Record<SkeletonColumnId, string>>;

const CatalogHeaderSkeleton = () => (
  <div className='flex shrink-0 flex-wrap items-end justify-between gap-4'>
    <Skeleton className='h-4 w-32' />
    <div className='flex min-w-64 flex-1 justify-end gap-1 sm:flex-none'>
      <Skeleton className='h-8 min-w-28 flex-1 sm:w-48 sm:flex-none' />
      <Skeleton className='h-8 min-w-28 flex-1 sm:w-48 sm:flex-none' />
    </div>
  </div>
);

const CatalogToolbarSkeleton = () => (
  <div className='flex min-h-12 flex-wrap items-center justify-between gap-2 px-2 py-2'>
    <div className='flex flex-1 flex-wrap items-center gap-2'>
      <Skeleton className='h-8 w-24' />
      <Skeleton className='h-8 w-36' />
    </div>
    <Skeleton className='h-8 w-full sm:w-64' />
  </div>
);

const CatalogTableSkeleton = () => (
  <div className='h-full w-full overflow-hidden rounded-lg border'>
    <div className='min-w-[48rem]'>
      <div className={`${tableGridClassName} h-10 border-b bg-background`}>
        {skeletonColumns.map(column => (
          <div
            key={column.id}
            className='flex min-w-0 items-center px-2'
          >
            <Skeleton className={`h-3 ${column.headerWidth}`} />
          </div>
        ))}
      </div>
      <div>
        {skeletonRowKeys.map((rowKey, rowIndex) => {
          const rowWidths =
            rowWidthPatterns[rowIndex % rowWidthPatterns.length] ?? rowWidthPatterns[0];
          const rowTone = rowIndex % 2 === 0 ? 'bg-gx-tile-light' : 'bg-gx-tile-dark';

          return (
            <div
              key={rowKey}
              className={`${tableGridClassName} h-6 border-b last:border-b-0 ${rowTone}`}
            >
              {skeletonColumns.map(column => (
                <div
                  key={column.id}
                  className='flex min-w-0 items-center px-2'
                >
                  <Skeleton className={`h-2.5 ${rowWidths[column.id]}`} />
                </div>
              ))}
            </div>
          );
        })}
      </div>
    </div>
  </div>
);

const CatalogSkeleton = () => (
  <section
    className='flex min-h-0 flex-1 flex-col'
    data-testid='catalog-skeleton'
  >
    <output
      aria-busy='true'
      aria-label='Loading catalog data'
      className='sr-only'
    >
      Loading catalog data
    </output>
    <div
      aria-hidden='true'
      className='flex min-h-0 flex-1 flex-col gap-3'
      data-testid='catalog-skeleton-visual'
    >
      <CatalogHeaderSkeleton />
      <DataTableSurface.Root>
        <DataTableSurface.Controls>
          <CatalogToolbarSkeleton />
        </DataTableSurface.Controls>
        <DataTableSurface.Body>
          <DataTableSurface.Content>
            <CatalogTableSkeleton />
          </DataTableSurface.Content>
        </DataTableSurface.Body>
      </DataTableSurface.Root>
    </div>
  </section>
);

export default CatalogSkeleton;
